import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';

import { loadProviderScript, type CaptchaProvider } from '../lib/captcha';

export interface CaptchaWidgetProps {
  provider: CaptchaProvider;
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: (err: Error) => void;
  onExpire?: () => void;
  // reCAPTCHA v3 action name; ignored by other providers.
  action?: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'invisible';
}

export interface CaptchaWidgetHandle {
  reset: () => void;
  // For invisible widgets — kicks off the challenge.
  execute: () => void;
}

// Renders the CAPTCHA widget for the configured provider. Lazy-loads the
// provider script (cached across mounts) and routes the resulting token to
// onVerify. For reCAPTCHA v3 it executes once on mount and re-executes on
// reset (since v3 has no rendered widget to reset). Mirrors the
// admin-portal's widget so token shape and lifecycle stay consistent.
export const CaptchaWidget = forwardRef<CaptchaWidgetHandle, CaptchaWidgetProps>(
  function CaptchaWidget(
    {
      provider,
      siteKey,
      onVerify,
      onError,
      onExpire,
      action = 'submit',
      theme = 'auto',
      size = 'normal',
    },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const onVerifyRef = useRef(onVerify);
    const onErrorRef = useRef(onError);
    const onExpireRef = useRef(onExpire);
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
    onExpireRef.current = onExpire;

    useEffect(() => {
      let cancelled = false;
      if (!siteKey || !containerRef.current) return;

      loadProviderScript(provider, siteKey)
        .then(() => {
          if (cancelled || !containerRef.current) return;
          renderWidget();
        })
        .catch((err: Error) => onErrorRef.current?.(err));

      function renderWidget() {
        const el = containerRef.current!;
        if (provider === 'turnstile' && window.turnstile) {
          widgetIdRef.current = window.turnstile.render(el, {
            sitekey: siteKey,
            theme,
            size,
            action,
            callback: (token: string) => onVerifyRef.current(token),
            'error-callback': () => onErrorRef.current?.(new Error('Turnstile error')),
            'expired-callback': () => onExpireRef.current?.(),
          });
        } else if (provider === 'hcaptcha' && window.hcaptcha) {
          widgetIdRef.current = window.hcaptcha.render(el, {
            sitekey: siteKey,
            theme: theme === 'auto' ? 'light' : theme,
            size,
            callback: (token: string) => onVerifyRef.current(token),
            'error-callback': () => onErrorRef.current?.(new Error('hCaptcha error')),
            'expired-callback': () => onExpireRef.current?.(),
          });
        } else if (provider === 'recaptcha_v3' && window.grecaptcha) {
          window.grecaptcha.ready(() => {
            window
              .grecaptcha!.execute(siteKey, { action })
              .then(token => onVerifyRef.current(token))
              .catch((err: unknown) =>
                onErrorRef.current?.(err instanceof Error ? err : new Error(String(err)))
              );
          });
        }
      }

      return () => {
        cancelled = true;
        if (widgetIdRef.current) {
          if (provider === 'turnstile' && window.turnstile) {
            window.turnstile.remove(widgetIdRef.current);
          } else if (provider === 'hcaptcha' && window.hcaptcha) {
            window.hcaptcha.remove(widgetIdRef.current);
          }
          widgetIdRef.current = null;
        }
      };
    }, [provider, siteKey, action, theme, size]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          if (!widgetIdRef.current) {
            // For reCAPTCHA v3, reset = re-execute to obtain a new token.
            if (provider === 'recaptcha_v3' && window.grecaptcha) {
              window.grecaptcha
                .execute(siteKey, { action })
                .then(token => onVerifyRef.current(token))
                .catch(() => onErrorRef.current?.(new Error('reCAPTCHA error')));
            }
            return;
          }
          if (provider === 'turnstile' && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
          } else if (provider === 'hcaptcha' && window.hcaptcha) {
            window.hcaptcha.reset(widgetIdRef.current);
          }
        },
        execute: () => {
          if (!widgetIdRef.current) return;
          if (provider === 'turnstile' && window.turnstile) {
            window.turnstile.execute(widgetIdRef.current);
          } else if (provider === 'hcaptcha' && window.hcaptcha) {
            window.hcaptcha.execute(widgetIdRef.current);
          }
        },
      }),
      [provider, siteKey, action]
    );

    return <div ref={containerRef} className="captcha-widget" data-testid="captcha-widget" />;
  }
);
